/**
 * YouStrip — the arrival ritual (W12 box 1).
 *
 * The first thing an officer sees walking in: where they stand, before
 * anything asks anything of them. Tier · rolling average · streak · this
 * week's calls, one quiet strip. Every piece hides when it has nothing
 * honest to say (no invented zeros, no placeholders).
 */

import { reportCrash } from '../../lib/crash';
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { initFirebaseClient } from '@copapp/firebase/client';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionStore } from '../../stores';
import { COLLECTIONS, SUBCOLLECTIONS } from '@copapp/core/constants';
import { isoWeekStart, type CertTier } from '@copapp/core';
import { getRhythmStreaks } from '../../services/weeklyDrill';
import { listSessions } from '../../services/sessions';
import { TIER_TEXT_COLORS } from '../training/CertificationPathCard';

interface Standing {
  tierName: string;
  tierKey: CertTier['key'];
  windowAvg: number | null;
  window: number;
}

interface Props {
  /**
   * DEMO MODE (docs/DEMO.md): sample values for the public /demo ride-along.
   * When set, no listeners attach and the strip renders the injected data.
   */
  demo?: { name: string; standing: Standing; streak: number; callsThisWeek: number };
}

export function YouStrip({ demo }: Props = {}) {
  const { user } = useAuth();
  const { officer } = useSessionStore();
  const [standing, setStanding] = useState<Standing | null>(demo?.standing ?? null);
  const [streak, setStreak] = useState<number | null>(demo?.streak ?? null);
  const [callsThisWeek, setCallsThisWeek] = useState<number | null>(demo?.callsThisWeek ?? null);

  // Standing — the same server-computed rolling block the tier card reads.
  useEffect(() => {
    if (demo) return;
    if (!user?.uid) return;
    const { db } = initFirebaseClient();
    const ref = doc(db, COLLECTIONS.OFFICERS, user.uid, SUBCOLLECTIONS.INSIGHTS_AGGREGATED, 'latest');
    return onSnapshot(ref, (snap) => {
      setStanding((snap.data()?.certification as Standing) ?? null);
    }, (err) => console.warn('you-strip standing listener error:', err.code));
  }, [user?.uid, demo]);

  // Streak — department rhythm only; solo officers simply don't show one.
  useEffect(() => {
    if (demo) return;
    const departmentId = (officer as { departmentId?: string | null } | null)?.departmentId;
    if (!departmentId) return;
    let cancelled = false;
    getRhythmStreaks(departmentId)
      .then((s) => { if (!cancelled && s.officerStreak > 0) setStreak(s.officerStreak); })
      .catch((e) => { reportCrash(e); /* the strip stands without it */ });
    return () => { cancelled = true; };
  }, [officer, demo]);

  // This week's calls — counted from the officer's own session list.
  useEffect(() => {
    if (demo) return;
    if (!user?.uid) return;
    let cancelled = false;
    const weekStart = isoWeekStart(new Date()).getTime();
    listSessions(user.uid, 50)
      .then((sessions) => {
        if (cancelled) return;
        setCallsThisWeek(sessions.filter(
          (s) => s.createdAt && new Date(s.createdAt).getTime() >= weekStart,
        ).length);
      })
      .catch((e) => { reportCrash(e); /* the strip stands without it */ });
    return () => { cancelled = true; };
  }, [user?.uid, demo]);

  const name = demo?.name || officer?.displayName || '';
  const hasAnything = name || standing || streak !== null || callsThisWeek !== null;
  if (!hasAnything) return null;

  return (
    <div
      data-testid="you-strip"
      className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-copapp-line pb-3"
    >
      {name && <span className="text-sm font-bold text-fg">{name}</span>}
      {standing && standing.windowAvg !== null && (
        <span className="text-xs text-fg-muted">
          ◈ <span className={`font-semibold ${TIER_TEXT_COLORS[standing.tierKey] ?? 'text-fg'}`}>{standing.tierName}</span>
          {` · ${standing.windowAvg.toFixed(1)}/10`}
        </span>
      )}
      {streak !== null && (
        <span className="text-xs text-fg-muted">◫ {streak}-week streak</span>
      )}
      {callsThisWeek !== null && (
        <span className="text-xs text-fg-faint">
          This week: {callsThisWeek} {callsThisWeek === 1 ? 'call' : 'calls'}
        </span>
      )}
    </div>
  );
}
